import { makeAutoObservable, runInAction } from "mobx";
import dayjs from "dayjs";
import "dayjs/locale/de";
import Parse from "parse";
import type {
  VolunteerTask,
  VolunteerSlot,
  VolunteerSignup,
  VolunteerSubject,
  CreateTaskData,
  UpdateTaskData,
  CreateSubjectData,
  UpdateSubjectData,
  CreateSlotData,
  UpdateSlotData,
  CreateSignupData,
} from "./types";

function mapSubject(obj: Parse.Object): VolunteerSubject {
  return {
    objectId: obj.id,
    title: obj.get("title") ?? "",
    order: obj.get("order") ?? 0,
    active: obj.get("active") !== false,
  };
}

function mapTask(obj: Parse.Object): VolunteerTask {
  return {
    objectId: obj.id,
    subjectId: obj.get("subject")?.id ?? undefined,
    title: obj.get("title") ?? "",
    description: obj.get("description") ?? undefined,
    notesTitle: obj.get("notesTitle") ?? undefined,
    order: obj.get("order") ?? 0,
    active: obj.get("active") !== false,
    contactPersonName: obj.get("contactPersonName") ?? undefined,
    contactPersonPhone: obj.get("contactPersonPhone") ?? undefined,
  };
}

function mapSlot(obj: Parse.Object): VolunteerSlot {
  return {
    objectId: obj.id,
    taskId: obj.get("task")?.id ?? "",
    label: obj.get("label") ?? undefined,
    description: obj.get("description") ?? undefined,
    startTime: obj.get("startTime") ?? undefined,
    endTime: obj.get("endTime") ?? undefined,
    capacity: obj.get("capacity") ?? undefined,
    order: obj.get("order") ?? 0,
  };
}

function formatTimeAsLabel(
  startTime?: Date,
  endTime?: Date,
): string | undefined {
  dayjs.locale("de");
  if (startTime && endTime)
    return `${dayjs(startTime).format("ddd HH:mm")}–${dayjs(endTime).format("HH:mm")}`;
  if (startTime) return dayjs(startTime).format("ddd HH:mm");
  return undefined;
}

function mapSignup(obj: Parse.Object): VolunteerSignup {
  return {
    objectId: obj.id,
    slotId: obj.get("slot")?.id ?? "",
    firstName: obj.get("firstName") ?? "",
    lastNameAbbrev: obj.get("lastNameAbbrev") ?? "",
    note: obj.get("note") ?? undefined,
  };
}

export class VolunteerStore {
  subjects: VolunteerSubject[] = [];
  tasks: VolunteerTask[] = [];
  slots: VolunteerSlot[] = [];
  signups: VolunteerSignup[] = [];
  loading = true;
  error: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  get activeSubjects(): VolunteerSubject[] {
    return this.subjects
      .filter((s) => s.active)
      .slice()
      .sort((a, b) => a.order - b.order || a.title.localeCompare(b.title));
  }

  get activeTasks(): VolunteerTask[] {
    return this.tasks
      .filter((t) => t.active)
      .slice()
      .sort((a, b) => a.order - b.order || a.title.localeCompare(b.title));
  }

  get taskGroups(): {
    subject: VolunteerSubject | null;
    tasks: VolunteerTask[];
  }[] {
    const groups: {
      subject: VolunteerSubject | null;
      tasks: VolunteerTask[];
    }[] = this.activeSubjects
      .map((subject) => ({
        subject,
        tasks: this.activeTasks.filter((t) => t.subjectId === subject.objectId),
      }))
      .filter((group) => group.tasks.length > 0);
    const subjectIds = new Set(this.activeSubjects.map((s) => s.objectId));
    const unassigned = this.activeTasks.filter(
      (t) => !t.subjectId || !subjectIds.has(t.subjectId),
    );
    if (unassigned.length > 0) {
      groups.push({ subject: null, tasks: unassigned });
    }
    return groups;
  }

  slotsForTask(taskId: string): VolunteerSlot[] {
    return this.slots
      .filter((s) => s.taskId === taskId)
      .slice()
      .sort(
        (a, b) =>
          a.order - b.order ||
          (a.startTime?.getTime() ?? 0) - (b.startTime?.getTime() ?? 0),
      );
  }

  signupsForSlot(slotId: string): VolunteerSignup[] {
    return this.signups.filter((s) => s.slotId === slotId);
  }

  isSlotFull(slotId: string): boolean {
    const slot = this.slots.find((s) => s.objectId === slotId);
    if (!slot?.capacity) return false;
    return this.signupsForSlot(slotId).length >= slot.capacity;
  }

  async loadAll() {
    runInAction(() => {
      this.loading = true;
      this.error = null;
    });
    try {
      const [subjects, tasks, slots, signups] = await Promise.all([
        new Parse.Query("SCH_VolunteerSubject").limit(10000).find(),
        new Parse.Query("SCH_VolunteerTask").limit(10000).find(),
        new Parse.Query("SCH_VolunteerSlot").limit(10000).find(),
        new Parse.Query("SCH_VolunteerSignup").limit(10000).find(),
      ]);
      runInAction(() => {
        this.subjects = subjects.map(mapSubject);
        this.tasks = tasks.map(mapTask);
        this.slots = slots.map(mapSlot);
        this.signups = signups.map(mapSignup);
        this.loading = false;
      });
    } catch (e: any) {
      runInAction(() => {
        this.error = e?.message ?? "Fehler beim Laden";
        this.loading = false;
      });
    }
  }

  async createSubject(data: CreateSubjectData) {
    const obj = new Parse.Object("SCH_VolunteerSubject");
    obj.set("title", data.title);
    obj.set("order", data.order ?? 0);
    obj.set("active", true);
    const saved = await obj.save();
    runInAction(() => {
      this.subjects.push(mapSubject(saved));
    });
  }

  async updateSubject(subjectId: string, data: UpdateSubjectData) {
    const obj = Parse.Object.fromJSON({
      className: "SCH_VolunteerSubject",
      objectId: subjectId,
    });
    if (data.title !== undefined) obj.set("title", data.title);
    if (data.order !== undefined) obj.set("order", data.order);
    if (data.active !== undefined) obj.set("active", data.active);
    const saved = await obj.save();
    runInAction(() => {
      const idx = this.subjects.findIndex((s) => s.objectId === subjectId);
      if (idx !== -1) this.subjects[idx] = mapSubject(saved);
    });
  }

  async reorderSubjects(orderedIds: string[]) {
    const changed = orderedIds
      .map((objectId, order) => ({ objectId, order }))
      .filter(
        ({ objectId, order }) =>
          this.subjects.find((s) => s.objectId === objectId)?.order !== order,
      );
    if (changed.length === 0) return;
    runInAction(() => {
      for (const { objectId, order } of changed) {
        const idx = this.subjects.findIndex((s) => s.objectId === objectId);
        if (idx !== -1) this.subjects[idx] = { ...this.subjects[idx], order };
      }
    });
    try {
      await Promise.all(
        changed.map(({ objectId, order }) => {
          const obj = Parse.Object.fromJSON({
            className: "SCH_VolunteerSubject",
            objectId,
          });
          obj.set("order", order);
          return obj.save();
        }),
      );
    } catch (e) {
      await this.loadAll();
      throw e;
    }
  }

  async deleteSubject(subjectId: string) {
    const affectedTaskIds = this.tasks
      .filter((t) => t.subjectId === subjectId)
      .map((t) => t.objectId!);
    runInAction(() => {
      this.subjects = this.subjects.filter((s) => s.objectId !== subjectId);
      this.tasks = this.tasks.map((t) =>
        t.subjectId === subjectId ? { ...t, subjectId: undefined } : t,
      );
    });
    await Promise.all([
      ...affectedTaskIds.map((taskId) => {
        const obj = Parse.Object.fromJSON({
          className: "SCH_VolunteerTask",
          objectId: taskId,
        });
        obj.unset("subject");
        return obj.save();
      }),
      Parse.Object.fromJSON({
        className: "SCH_VolunteerSubject",
        objectId: subjectId,
      }).destroy(),
    ]);
  }

  async createTask(data: CreateTaskData) {
    const obj = new Parse.Object("SCH_VolunteerTask");
    obj.set("title", data.title);
    if (data.description) obj.set("description", data.description);
    if (data.notesTitle) obj.set("notesTitle", data.notesTitle);
    if (data.contactPersonName)
      obj.set("contactPersonName", data.contactPersonName);
    if (data.contactPersonPhone)
      obj.set("contactPersonPhone", data.contactPersonPhone);
    if (data.subjectId) {
      obj.set(
        "subject",
        Parse.Object.fromJSON({
          className: "SCH_VolunteerSubject",
          objectId: data.subjectId,
        }),
      );
    }
    obj.set("order", data.order ?? 0);
    obj.set("active", true);
    const saved = await obj.save();
    runInAction(() => {
      this.tasks.push(mapTask(saved));
    });
  }

  async updateTask(taskId: string, data: UpdateTaskData) {
    const obj = Parse.Object.fromJSON({
      className: "SCH_VolunteerTask",
      objectId: taskId,
    });
    if (data.title !== undefined) obj.set("title", data.title);
    if ("description" in data) {
      if (data.description) obj.set("description", data.description);
      else obj.unset("description");
    }
    if ("notesTitle" in data) {
      if (data.notesTitle) obj.set("notesTitle", data.notesTitle);
      else obj.unset("notesTitle");
    }
    if ("contactPersonName" in data) {
      if (data.contactPersonName)
        obj.set("contactPersonName", data.contactPersonName);
      else obj.unset("contactPersonName");
    }
    if ("contactPersonPhone" in data) {
      if (data.contactPersonPhone)
        obj.set("contactPersonPhone", data.contactPersonPhone);
      else obj.unset("contactPersonPhone");
    }
    if (data.order !== undefined) obj.set("order", data.order);
    if (data.active !== undefined) obj.set("active", data.active);
    if ("subjectId" in data) {
      if (data.subjectId) {
        obj.set(
          "subject",
          Parse.Object.fromJSON({
            className: "SCH_VolunteerSubject",
            objectId: data.subjectId,
          }),
        );
      } else {
        obj.unset("subject");
      }
    }
    const saved = await obj.save();
    runInAction(() => {
      const idx = this.tasks.findIndex((t) => t.objectId === taskId);
      if (idx !== -1) this.tasks[idx] = mapTask(saved);
    });
  }

  async reorderTasks(orderedIds: string[]) {
    const changed = orderedIds
      .map((objectId, order) => ({ objectId, order }))
      .filter(
        ({ objectId, order }) =>
          this.tasks.find((t) => t.objectId === objectId)?.order !== order,
      );
    if (changed.length === 0) return;
    runInAction(() => {
      for (const { objectId, order } of changed) {
        const idx = this.tasks.findIndex((t) => t.objectId === objectId);
        if (idx !== -1) this.tasks[idx] = { ...this.tasks[idx], order };
      }
    });
    try {
      await Promise.all(
        changed.map(({ objectId, order }) => {
          const obj = Parse.Object.fromJSON({
            className: "SCH_VolunteerTask",
            objectId,
          });
          obj.set("order", order);
          return obj.save();
        }),
      );
    } catch (e) {
      await this.loadAll();
      throw e;
    }
  }

  async deleteTask(taskId: string) {
    const slotIds = this.slotsForTask(taskId).map((s) => s.objectId);
    runInAction(() => {
      this.tasks = this.tasks.filter((t) => t.objectId !== taskId);
      this.slots = this.slots.filter((s) => s.taskId !== taskId);
      this.signups = this.signups.filter((s) => !slotIds.includes(s.slotId));
    });
    await Parse.Object.fromJSON({
      className: "SCH_VolunteerTask",
      objectId: taskId,
    }).destroy();
  }

  async createSlot(data: CreateSlotData) {
    const obj = new Parse.Object("SCH_VolunteerSlot");
    obj.set(
      "task",
      Parse.Object.fromJSON({
        className: "SCH_VolunteerTask",
        objectId: data.taskId,
      }),
    );
    const label = data.label || formatTimeAsLabel(data.startTime, data.endTime);
    if (label) obj.set("label", label);
    if (data.description) obj.set("description", data.description);
    if (data.startTime) obj.set("startTime", data.startTime);
    if (data.endTime) obj.set("endTime", data.endTime);
    if (data.capacity !== undefined) obj.set("capacity", data.capacity);
    obj.set("order", data.order ?? 0);
    const saved = await obj.save();
    runInAction(() => {
      this.slots.push(mapSlot(saved));
    });
  }

  async updateSlot(slotId: string, data: UpdateSlotData) {
    const obj = Parse.Object.fromJSON({
      className: "SCH_VolunteerSlot",
      objectId: slotId,
    });
    if (
      data.label !== undefined ||
      data.startTime !== undefined ||
      data.endTime !== undefined
    ) {
      obj.set(
        "label",
        data.label || formatTimeAsLabel(data.startTime, data.endTime),
      );
    }
    if (data.description !== undefined)
      obj.set("description", data.description);
    if (data.startTime !== undefined) obj.set("startTime", data.startTime);
    if (data.endTime !== undefined) obj.set("endTime", data.endTime);
    if (data.capacity !== undefined) obj.set("capacity", data.capacity);
    if (data.order !== undefined) obj.set("order", data.order);
    const saved = await obj.save();
    runInAction(() => {
      const idx = this.slots.findIndex((s) => s.objectId === slotId);
      if (idx !== -1) this.slots[idx] = mapSlot(saved);
    });
  }

  async deleteSlot(slotId: string) {
    runInAction(() => {
      this.slots = this.slots.filter((s) => s.objectId !== slotId);
      this.signups = this.signups.filter((s) => s.slotId !== slotId);
    });
    await Parse.Object.fromJSON({
      className: "SCH_VolunteerSlot",
      objectId: slotId,
    }).destroy();
  }

  async createSignup(data: CreateSignupData) {
    if (this.isSlotFull(data.slotId)) {
      throw new Error("Diese Schicht ist leider schon voll.");
    }
    const lastNameAbbrev = data.lastName.trim()
      ? `${data.lastName.trim().charAt(0).toUpperCase()}.`
      : "";
    const obj = new Parse.Object("SCH_VolunteerSignup");
    obj.set(
      "slot",
      Parse.Object.fromJSON({
        className: "SCH_VolunteerSlot",
        objectId: data.slotId,
      }),
    );
    obj.set("firstName", data.firstName);
    obj.set("lastName", data.lastName);
    obj.set("lastNameAbbrev", lastNameAbbrev);
    if (data.email) obj.set("email", data.email);
    if (data.phone) obj.set("phone", data.phone);
    if (data.note) obj.set("note", data.note);
    const saved = await obj.save();
    runInAction(() => {
      this.signups.push(mapSignup(saved));
    });
  }

  async deleteSignup(signupId: string) {
    runInAction(() => {
      this.signups = this.signups.filter((s) => s.objectId !== signupId);
    });
    await Parse.Object.fromJSON({
      className: "SCH_VolunteerSignup",
      objectId: signupId,
    }).destroy();
  }

  async emailContacts(
    target: { slotId?: string; taskId?: string },
    targetEmail: string,
  ) {
    await Parse.Cloud.run("sendVolunteerContacts", { ...target, targetEmail });
  }
}
